import {Attribute} from '../../../components/ObjectAttribute';
import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';

const searchTransferOrdersRequest: RequestExample = {
    title: {
        verb: 'GET',
        endpointUrl: '/api/transfer-orders',
    },
    curl:
        `curl --location \\
--request GET '<YOUR SERVER>/api/transfer-orders' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>'
`,
};

const searchTransferOrdersResponse: Json = {
    title: 'RESPONSE',
    json: {
        'totalCount': 12,
        'pageNumber': 1,
        'pageSize': 2,
        'results': [
            {
                'id': 1,
                'number': 'TO-00001',
                'status': 'Issued',
                'type': 'Ship',
                'fromLocationGroup': 'Main Warehouse',
                'toLocationGroup': 'East Warehouse',
                'dateIssued': '2024-03-01T10:00:00.000-0700',
                'dateScheduled': '2024-03-05T00:00:00.000-0700',
                'readOnly': false
            },
            {
                'id': 2,
                'number': 'TO-00002',
                'status': 'Fulfilled',
                'type': 'Move',
                'fromLocationGroup': 'East Warehouse',
                'toLocationGroup': 'Main Warehouse',
                'dateIssued': '2024-03-02T09:30:00.000-0700',
                'dateScheduled': '2024-03-06T00:00:00.000-0700',
                'readOnly': true
            }
        ],
        'totalPages': 6
    },
};

const searchTransferOrdersQueryParameters: Attribute[] = [
    {
        name: 'pageNumber',
        type: 'integer',
        optional: true,
        description: 'The current page of the results.'
    },
    {
        name: 'pageSize',
        type: 'integer',
        optional: true,
        description: 'The number of returned results per page. (Default 100)'
    },
    {
        name: 'number',
        type: 'string',
        optional: true,
        description: 'The transfer order number.'
    },
    {
        name: 'type',
        type: 'string: \'All\' | \'Ship\' | \'Move\' | \'Putaway\'',
        optional: true,
        description: 'The transfer order type.'
    },
    {
        name: 'status',
        type: 'string: \'All\' | \'All Open\' | \'Entered\' | \'Request\' | \'Issued\' | \'Picking\' | \'Partial\' | \'Picked\' | \'Shipped\' | \'Fulfilled\' | \'Closed Short\' | \'Void\'',
        optional: true,
        description: 'The transfer order status.'
    },
    {
        name: 'username',
        type: 'string',
        optional: true,
        description: 'The username associated with the transfer order.'
    },
    {
        name: 'fromLocationGroup',
        type: 'string',
        optional: true,
        description: 'The ID or name of the origin location group. The value is first parsed as an integer to search by ID; if that fails, it searches by name.'
    },
    {
        name: 'toLocationGroup',
        type: 'string',
        optional: true,
        description: 'The ID or name of the destination location group. The value is first parsed as an integer to search by ID; if that fails, it searches by name.'
    },
    {
        name: 'issuedFrom',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The start of the issued date range. Both issuedFrom and issuedTo must be provided for the filter to apply.'
    },
    {
        name: 'issuedTo',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The end of the issued date range. Both issuedFrom and issuedTo must be provided for the filter to apply.'
    },
    {
        name: 'scheduledFrom',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The start of the scheduled date range. Both scheduledFrom and scheduledTo must be provided for the filter to apply.'
    },
    {
        name: 'scheduledTo',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The end of the scheduled date range. Both scheduledFrom and scheduledTo must be provided for the filter to apply.'
    },
    {
        name: 'receivedFrom',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The start of the received date range. Both receivedFrom and receivedTo must be provided for the filter to apply.'
    },
    {
        name: 'receivedTo',
        type: 'date: \'yyyy-MM-dd\'',
        optional: true,
        description: 'The end of the received date range. Both receivedFrom and receivedTo must be provided for the filter to apply.'
    },
    {
        name: 'containingPartNumber',
        type: 'string',
        optional: true,
        description: 'Filters orders that contain a part with this number.'
    },
    {
        name: 'containingPartDescription',
        type: 'string',
        optional: true,
        description: 'Filters orders that contain a part with this description.'
    },
];

const searchTransferOrdersBodyAttributes: Attribute[] = [
    {
        name: 'customFields',
        type: 'list',
        description: 'A list of custom fields to filter by.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identifier for the custom field.',
            },
            {
                name: 'value',
                type: 'string',
                description: 'The custom field\'s value.'
            },
        ]
    }
];

const data: RequestDefinitionData = {
    id: 'search-transfer-orders',
    title: 'Search for transfer orders',
    description: 'Searches for transfer orders. Optionally pass a request body with customFields to filter by custom field values.',
    endpoint: {
        verb: 'GET',
        path: '/transfer-orders',
        tooltip: 'Search for transfer orders',
    },
    attributes: searchTransferOrdersBodyAttributes,
    requestObject: searchTransferOrdersRequest,
    responseObject: searchTransferOrdersResponse,
    queryParameters: searchTransferOrdersQueryParameters,
};

export default data;
